import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class ColorPicker extends Component {
    /** @type {string | undefined} */
    #pendingRequestUrl;

    /** @type {AbortController | undefined} */
    #abortController;

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener("change", this.colorChanged.bind(this));
    }

    /**
     * Handles the variant change event.
     * @param {Event} event - The variant change event.
     */
    colorChanged(event) {
        // What are we missing?
        // - need to support swatches potentially, we will need to check on what's needed for that.
        if (!(event.target instanceof HTMLElement)) return;

        if (this.productTemplate !== "custom-size") {
            this.updateSelectedOption(event.target);
        }

        const isOnProductPage =
            Theme.template.name === "product" &&
            !event.target.closest("product-card") &&
            !event.target.closest("quick-add-dialog") &&
            !event.target.closest("quick-add-drawer");

        // Morph the entire main content for combined listings child products, because changing the product
        // might also change other sections depending on recommendations, metafields, etc.
        const newUrl = event.target.dataset.connectedProductUrl;

        const loadsNewProduct = true;

        if (this.productTemplate === "custom-size") {
            // For custom size products, we need to refresh page
            window.location.href = newUrl;
            return;
        }

        this.fetchUpdatedSection(
            this.buildRequestUrl(event.target),
            loadsNewProduct,
        );

        const url = new URL(window.location.href);

        if (isOnProductPage) {
            if (event.target.dataset.variantId) {
                url.searchParams.set("variant", event.target.dataset.variantId);
            } else {
                url.searchParams.delete("variant");
            }
        }

        // Change the path if the option is connected to another product via combined listing.
        if (loadsNewProduct) {
            url.pathname = newUrl;
        }

        if (url.href !== window.location.href) {
            history.replaceState({}, "", url.toString());
        }
    }

    /**
     * Updates the selected option.
     * @param {string | Element} target - The target element.
     */
    updateSelectedOption(target) {
        if (typeof target === "string") {
            const targetElement = this.querySelector(
                `[data-option-value-id="${target}"]`,
            );

            if (!targetElement) throw new Error("Target element not found");

            target = targetElement;
        }

        if (target instanceof HTMLInputElement) {
            target.checked = true;
        }

        if (target instanceof HTMLSelectElement) {
            const newValue = target.value;
            const newSelectedOption = Array.from(target.options).find(
                (option) => option.value === newValue,
            );

            if (!newSelectedOption) throw new Error("Option not found");

            for (const option of target.options) {
                option.removeAttribute("selected");
            }

            newSelectedOption.setAttribute("selected", "selected");
        }
    }

    /**
     * Builds the request URL.
     * @param {HTMLElement} selectedOption - The selected option.
     * @param {string | null} [source] - The source.
     * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
     * @returns {string} The request URL.
     */
    buildRequestUrl(
        selectedOption,
        source = null,
        sourceSelectedOptionsValues = [],
    ) {
        // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
        // We create a new URL and abort the previous fetch request if it's still pending.
        let productUrl =
            selectedOption.dataset.connectedProductUrl ||
            this.#pendingRequestUrl ||
            this.dataset.productUrl;
        this.#pendingRequestUrl = productUrl;
        const params = [];

        if (this.selectedOptionsValues.length && !source) {
            params.push(
                `option_values=${this.selectedOptionsValues.join(",")}`,
            );
        } else if (source === "product-card") {
            if (this.selectedOptionsValues.length) {
                params.push(
                    `option_values=${sourceSelectedOptionsValues.join(",")}`,
                );
            } else {
                params.push(
                    `option_values=${selectedOption.dataset.optionValueId}`,
                );
            }
        }

        // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
        if (
            this.closest("quick-add-component") ||
            this.closest("swatches-variant-picker-component")
        ) {
            if (productUrl?.includes("?")) {
                productUrl = productUrl.split("?")[0];
            }
            return `${productUrl}?section_id=section-rendering-product-card&${params.join("&")}`;
        }
        return `${productUrl}?${params.join("&")}`;
    }

    /**
     * Fetches the updated section.
     * @param {string} requestUrl - The request URL.
     * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
     */
    fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
        // We use this to abort the previous fetch request if it's still pending.
        this.#abortController?.abort();
        this.#abortController = new AbortController();

        fetch(requestUrl, { signal: this.#abortController.signal })
            .then((response) => response.text())
            .then((responseText) => {
                this.#pendingRequestUrl = undefined;
                const html = new DOMParser().parseFromString(
                    responseText,
                    "text/html",
                );
                // Defer is only useful for the initial rendering of the page. Remove it here.
                html.querySelector("overflow-list[defer]")?.removeAttribute(
                    "defer",
                );

                const textContent = html.querySelector(
                    `new-variant-picker script[type="application/json"]`,
                )?.textContent;
                if (!textContent && this.productTemplate === "default") return;

                if (shouldMorphMain) {
                    this.updateMain(html);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    if (window.wishlist) {
                        // active if wishlist is enabled, ensures that the wishlist buttons are correctly updated after the main content is morphed.
                        //window.wishlist.onReloadWidgets();
                    }
                    if (window.reviewApp) {
                        window.reviewApp.onReloadWidgets();
                    }
                    this.dispatchEvent(
                        new CustomEvent("on:color-item:change", {
                            bubbles: true,
                            detail: {
                                productHandle: this.selectedOptionId,
                            },
                        }),
                    );
                } else {
                    const newProduct = this.updateVariantPicker(html);

                    // We grab the variant object from the response and dispatch an event with it.
                    if (this.selectedOptionId) {
                        this.dispatchEvent(
                            new VariantUpdateEvent(
                                JSON.parse(textContent),
                                this.selectedOptionId,
                                {
                                    html,
                                    productId: this.dataset.productId ?? "",
                                    newProduct,
                                },
                            ),
                        );
                    }
                }
            })
            .catch((error) => {
                if (error.name === "AbortError") {
                    console.log("Fetch aborted by user");
                } else {
                    console.error(error);
                }
            });
    }

    /**
     * @typedef {Object} NewProduct
     * @property {string} id
     * @property {string} url
     */

    /**
     * Re-renders the variant picker.
     * @param {Document} newHtml - The new HTML.
     * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
     */
    updateVariantPicker(newHtml) {
        /** @type {NewProduct | undefined} */
        let newProduct;

        const newVariantPickerSource = newHtml.querySelector(
            this.tagName.toLowerCase(),
        );

        if (!newVariantPickerSource) {
            throw new Error("No new variant picker source found");
        }

        // For combined listings, the product might have changed, so update the related data attribute.
        if (newVariantPickerSource instanceof HTMLElement) {
            const newProductId = newVariantPickerSource.dataset.productId;
            const newProductUrl = newVariantPickerSource.dataset.productUrl;

            if (
                newProductId &&
                newProductUrl &&
                this.dataset.productId !== newProductId
            ) {
                newProduct = { id: newProductId, url: newProductUrl };
            }

            this.dataset.productId = newProductId;
            this.dataset.productUrl = newProductUrl;
        }

        morph(this, newVariantPickerSource);

        return newProduct;
    }

    /**
     * Re-renders the entire main content.
     * @param {Document} newHtml - The new HTML.
     */
    updateMain(newHtml) {
        const main = document.querySelector("main");
        const newMain = newHtml.querySelector("main");
        const modalTemplate = newHtml.querySelector(".js-media-zoom-template");
        const galleryZoomModal = document.querySelector(".gallery-zoom-modal");
        if (galleryZoomModal) galleryZoomModal.remove();

        if (!main || !newMain) {
            throw new Error("No new main source found");
        }

        morph(main, newMain);
        // Force lazy-loaded section stylesheets to apply
        main.querySelectorAll('link[rel="stylesheet"][media="print"]').forEach(
            (link) => {
                link.media = "all";
            },
        );

        setTimeout(() => {
            const mediaGallery = document.querySelector(
                ".product-gallery-and-more media-gallery",
            );
            if (mediaGallery) {
                if (modalTemplate) {
                    mediaGallery.galleryModal =
                        modalTemplate?.content?.firstElementChild?.cloneNode(
                            true,
                        );
                }
            }
        }, 50);
    }

    /**
     * Gets the selected option.
     * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
     */
    get selectedOption() {
        const selectedOption = this.querySelector(
            "select option[selected], fieldset input:checked",
        );

        if (
            !(
                selectedOption instanceof HTMLInputElement ||
                selectedOption instanceof HTMLOptionElement
            )
        ) {
            return undefined;
        }

        return selectedOption;
    }

    /**
     * Gets the selected option ID.
     * @returns {string | undefined} The selected option ID.
     */
    get selectedOptionId() {
        const { selectedOption } = this;
        if (!selectedOption) return undefined;
        const { optionValueId } = selectedOption.dataset;

        if (!optionValueId) {
            throw new Error("No option value ID found");
        }

        return optionValueId;
    }

    /**
     * Gets the selected options values.
     * @returns {string[]} The selected options values.
     */
    get selectedOptionsValues() {
        /** @type HTMLElement[] */
        const selectedOptions = Array.from(
            this.querySelectorAll(
                "select option[selected], fieldset input:checked",
            ),
        );

        return selectedOptions.map((option) => {
            const { optionValueId } = option.dataset;

            if (!optionValueId) throw new Error("No option value ID found");

            return optionValueId;
        });
    }

    get shapeOption() {
        return this.querySelector('[data-option-name="Shape"]');
    }

    get sizeOptions() {
        return this.querySelectorAll('[data-option-name="Title"] .opt-label');
    }

    get productTemplate() {
        return this.dataset.productTemplate;
    }
}

if (!customElements.get("color-picker")) {
    customElements.define("color-picker", ColorPicker);
}
